import _ from 'lodash';
import { sqDurationStore } from '@/core/core.stores';
import { BASE_TOOL_COMMON_PROPS } from '@/toolSelection/baseTool.constants';
import { BaseToolStore } from '@/toolSelection/baseTool.store';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import {
  BinMode,
  ControlChartType,
  DEFAULT_ALIGNMENT_OPTION,
  DEFAULT_BIN_WIDTH,
  DEFAULT_BIN_WIDTH_UOM,
  DEFAULT_NUMBER_OF_BINS,
  DEFAULT_RATIONAL_SUBGROUP_SIZE,
} from '@/tools/statisticsSummary/statisticsSummary.constants';
import type { BaseToolPersistedState, ToolItem, ToolParameter } from '@/toolSelection/baseTool.store';
import type { RunRules } from '@/tools/statisticsSummary/statisticsSummary.constants';
import type { ValueWithUnitsItem } from '@/trend/ValueWithUnits.atom';
import type { Capsule } from '@/utilities/datetime.constants';

interface StatisticsSummaryState extends BaseToolPersistedState {
  inputSignal?: ToolItem | null;
  condition?: ToolItem | null;
  alignmentCondition?: ToolItem | null;
  alignmentMaximumDurationOverride?: ValueWithUnitsItem | undefined;
  windowStart: number;
  windowEnd: number;
  window: any; // computed property using monkey()
  color: string;
  binMode: BinMode;
  binWidth: number;
  binWidthUom: string;
  numberOfBins: number;
  upperSpecificationLimit: number | null;
  lowerSpecificationLimit: number | null;
  target: number | null;
  subgroupSize: number | null;
  controlChart: ControlChartType | null;
  rationalSubgroupSize: number;
  runRules: RunRules[];
  showSpcChartSettings: boolean;
  manualAlignmentConditionName?: string;
  cpkSubgroupSizeIsManual: boolean;
}

export type StatisticsSummaryDehydratedState = Omit<StatisticsSummaryState, 'window'>;

export class StatisticsSummaryStore extends BaseToolStore {
  static readonly storeName = 'sqStatisticsSummaryStore';
  type = TREND_TOOLS.STATISTICS_SUMMARY;
  parameterDefinitions = {
    condition: {
      predicate: ['name', 'condition'],
    },
    alignmentCondition: {
      predicate: ['name', 'alignmentCondition'],
    },
    inputSignal: {
      predicate: (parameter: ToolParameter) =>
        (parameter.name ?? '') !== 'condition' && (parameter.name ?? '') !== 'alignmentCondition',
    },
  };

  initialize(): void {
    const initialState: StatisticsSummaryState = {
      ...BASE_TOOL_COMMON_PROPS,
      alignmentCondition: DEFAULT_ALIGNMENT_OPTION,
      alignmentMaximumDurationOverride: { value: 40, units: 'h' },
      windowStart: sqDurationStore.displayRange.start.valueOf(),
      windowEnd: sqDurationStore.displayRange.end.valueOf(),
      window: this.monkey(['windowStart'], ['windowEnd'], (startTime, endTime) => ({ startTime, endTime })),
      color: '',
      binMode: BinMode.SIZE,
      binWidth: DEFAULT_BIN_WIDTH,
      binWidthUom: DEFAULT_BIN_WIDTH_UOM,
      numberOfBins: DEFAULT_NUMBER_OF_BINS,
      upperSpecificationLimit: null,
      lowerSpecificationLimit: null,
      target: null,
      subgroupSize: null,
      controlChart: ControlChartType.IMR,
      runRules: [],
      rationalSubgroupSize: DEFAULT_RATIONAL_SUBGROUP_SIZE,
      showSpcChartSettings: false,
      manualAlignmentConditionName: undefined,
      cpkSubgroupSizeIsManual: false,
    };
    this.state = this.immutable(initialState);
  }

  get inputSignal(): ToolItem & { id: string } {
    return this.state.get('inputSignal');
  }

  get condition(): ToolItem & { id: string } {
    return this.state.get('condition');
  }

  get alignmentCondition(): ToolItem & { id: string } {
    return this.state.get('alignmentCondition');
  }

  get window(): Capsule {
    return this.state.get('window');
  }

  get color(): string {
    return this.state.get('color');
  }

  get binMode(): BinMode {
    return this.state.get('binMode');
  }

  get binWidth(): number {
    return this.state.get('binWidth');
  }

  get binWidthUom(): string {
    return this.state.get('binWidthUom');
  }

  get numberOfBins(): number {
    return this.state.get('numberOfBins');
  }

  get upperSpecificationLimit(): number {
    return this.state.get('upperSpecificationLimit');
  }

  get lowerSpecificationLimit(): number {
    return this.state.get('lowerSpecificationLimit');
  }

  get target(): number {
    return this.state.get('target');
  }

  get subgroupSize(): number | null {
    return this.state.get('subgroupSize');
  }

  get controlChart(): ControlChartType {
    return this.state.get('controlChart');
  }

  get runRules(): RunRules[] {
    return this.state.get('runRules');
  }

  get rationalSubgroupSize(): number {
    return this.state.get('rationalSubgroupSize');
  }

  get showSpcChartSettings(): boolean {
    return this.state.get('showSpcChartSettings');
  }

  get manualAlignmentConditionName(): string | undefined {
    return this.state.get('manualAlignmentConditionName');
  }

  get cpkSubgroupSizeIsManual(): boolean {
    return this.state.get('cpkSubgroupSizeIsManual');
  }

  get alignmentMaximumDurationOverride(): ValueWithUnitsItem | undefined {
    return this.state.get('alignmentMaximumDurationOverride');
  }

  dehydrate(): StatisticsSummaryDehydratedState {
    return this.state.serialize();
  }

  rehydrate(dehydratedState: Partial<StatisticsSummaryDehydratedState>): void {
    // Migration: Convert old alignmentSelectionIsManual (boolean) to new manualAlignmentConditionName (string)
    // This ensures old workbooks with manually selected alignment conditions (especially "Weekly")
    // retain their selection on first edit after upgrade
    if ('alignmentSelectionIsManual' in dehydratedState) {
      const oldState = dehydratedState as any;
      if (
        oldState.alignmentSelectionIsManual === true &&
        dehydratedState.alignmentCondition?.name &&
        !dehydratedState.manualAlignmentConditionName
      ) {
        dehydratedState.manualAlignmentConditionName = dehydratedState.alignmentCondition.name;
      }
      // Clean up the old field so it doesn't persist in future saves
      delete oldState.alignmentSelectionIsManual;
    }

    this.state.merge(dehydratedState);
  }

  setSampleWindow(payload: { window: Capsule }): void {
    this.state.set('windowStart', _.get(payload.window, 'startTime'));
    this.state.set('windowEnd', _.get(payload.window, 'endTime'));
  }

  setColor(payload: { color: string }): void {
    this.state.set('color', payload.color);
  }

  setBinMode(payload: { binMode: BinMode }): void {
    this.state.set('binMode', payload.binMode);
  }

  setBinWidth(payload: { binWidth: number }): void {
    this.state.set('binWidth', payload.binWidth);
  }

  setBinWidthUom(payload: { binWidthUom: string }): void {
    this.state.set('binWidthUom', payload.binWidthUom);
  }

  setNumberOfBins(payload: { numberOfBins: number }): void {
    this.state.set('numberOfBins', payload.numberOfBins);
  }

  setUpperSpecificationLimit(payload: { upperSpecificationLimit: number }): void {
    this.state.set('upperSpecificationLimit', payload.upperSpecificationLimit);
  }

  setLowerSpecificationLimit(payload: { lowerSpecificationLimit: number }): void {
    this.state.set('lowerSpecificationLimit', payload.lowerSpecificationLimit);
  }

  setTarget(payload: { target: number }): void {
    this.state.set('target', payload.target);
  }

  setSubgroupSize(payload: { subgroupSize: number | null }): void {
    this.state.set('subgroupSize', payload.subgroupSize);
  }

  setControlChart(payload: { controlChart: ControlChartType }): void {
    this.state.set('controlChart', payload.controlChart);
  }

  setRunRules(payload: { runRules: RunRules[] }): void {
    this.state.set('runRules', payload.runRules);
  }

  setRationalSubgroupSize(payload: { rationalSubgroupSize: number }): void {
    this.state.set('rationalSubgroupSize', payload.rationalSubgroupSize);
  }

  setShowSpcChartSettings(payload: { showSpcChartSettings: boolean }): void {
    this.state.set('showSpcChartSettings', payload.showSpcChartSettings);
  }

  setManualAlignmentConditionName(payload: { manualAlignmentConditionName: string | undefined }): void {
    this.state.set('manualAlignmentConditionName', payload.manualAlignmentConditionName);
  }

  setCpkSubgroupSizeIsManual(payload: { cpkSubgroupSizeIsManual: boolean }): void {
    this.state.set('cpkSubgroupSizeIsManual', payload.cpkSubgroupSizeIsManual);
  }

  setAlignmentMaximumDurationOverride(payload: { alignmentMaximumDurationOverride: ValueWithUnitsItem }): void {
    this.state.set('alignmentMaximumDurationOverride', payload.alignmentMaximumDurationOverride);
  }

  protected readonly handlers = {
    ...this.baseHandlers,
    SS_SET_COLOR: this.setColor,
    SS_SET_BIN_WIDTH: this.setBinWidth,
    SS_SET_BIN_WIDTH_UOM: this.setBinWidthUom,
    SS_SET_BIN_MODE: this.setBinMode,
    SS_SET_NUMBER_OF_BINS: this.setNumberOfBins,
    SS_SET_SAMPLE_WINDOW: this.setSampleWindow,
    SS_SET_UPPER_SPECIFICATION_LIMIT: this.setUpperSpecificationLimit,
    SS_SET_LOWER_SPECIFICATION_LIMIT: this.setLowerSpecificationLimit,
    SS_SET_TARGET: this.setTarget,
    SS_SET_SUBGROUP_SIZE: this.setSubgroupSize,
    SS_SET_CONTROL_CHART: this.setControlChart,
    SS_SET_RUN_RULES: this.setRunRules,
    SS_SET_RATIONAL_SUBGROUP_SIZE: this.setRationalSubgroupSize,
    SS_SET_SHOW_SPC_CHART_SETTINGS: this.setShowSpcChartSettings,
    SS_SET_MANUAL_ALIGNMENT_CONDITION_NAME: this.setManualAlignmentConditionName,
    SS_SET_CPK_SUBGROUP_SIZE_IS_MANUAL: this.setCpkSubgroupSizeIsManual,
    SS_SET_ALIGNMENT_MAXIMUM_DURATION_OVERRIDE: this.setAlignmentMaximumDurationOverride,
  };
}
